import type { AlertSettingsInput, ConnectionTestResult } from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { isSameValue } from '@/lib/is-same-value.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Input } from '@/components/ui/input.js';
import { Switch } from '@/components/ui/switch.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import {
  fetchAlertSettings,
  testAlertEmail,
  testAlertSlack,
  updateAlertSettings,
} from './api-alerts.js';
import { settingsKeys } from './query-keys.js';
import { ConnectionTestMessage, SettingsSectionSkeleton } from './settings-parts.js';

interface FormValues {
  emails: string[];
  slackEnabled: boolean;
}

function TestButton({
  label,
  onTest,
}: {
  label: string;
  onTest: () => Promise<ConnectionTestResult>;
}): JSX.Element {
  const [result, setResult] = useState<ConnectionTestResult | null>(null);
  const mutation = useMutation({
    mutationFn: onTest,
    onSuccess: (data) => {
      setResult(data);
      if (data.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <Button
        type="button"
        variant="outline"
        isLoading={mutation.isPending}
        onClick={() => {
          mutation.mutate();
        }}
      >
        {label}
      </Button>
      {result ? (
        <ConnectionTestMessage
          ok={result.ok}
          message={result.message}
          detail={result.detail}
          durationMs={result.durationMs}
        />
      ) : null}
    </div>
  );
}

/** Onglet Alertes — `02` §12.5. */
export function AlertesTab(): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: settingsKeys.alerts, queryFn: fetchAlertSettings });
  const [values, setValues] = useState<FormValues | null>(null);
  const [emailDraft, setEmailDraft] = useState('');

  useEffect(() => {
    if (query.data) {
      setValues({
        emails: query.data.emails,
        slackEnabled: query.data.slackEnabled,
      });
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: updateAlertSettings,
    onSuccess: (data) => {
      toast.success('Réglages d’alertes mis à jour.');
      queryClient.setQueryData(settingsKeys.alerts, data);
      setValues({
        emails: data.emails,
        slackEnabled: data.slackEnabled,
      });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  if (query.isPending || !values) {
    return (
      <div className="flex flex-col gap-6">
        <SettingsSectionSkeleton fields={2} />
        <SettingsSectionSkeleton fields={2} />
      </div>
    );
  }
  if (query.isError) {
    return (
      <ErrorState
        message={
          query.error instanceof ApiError
            ? query.error.message
            : 'Une erreur technique est survenue.'
        }
        detail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const isDirty = !isSameValue(values, {
    emails: query.data.emails,
    slackEnabled: query.data.slackEnabled,
  });

  function addEmail(): void {
    const trimmed = emailDraft.trim();
    if (!trimmed) {
      return;
    }
    if (!z.email().safeParse(trimmed).success) {
      toast.error(`« ${trimmed} » n’est pas une adresse email valide.`);
      return;
    }
    setValues((prev) => {
      if (!prev) {
        return prev;
      }
      if (prev.emails.includes(trimmed)) {
        return prev;
      }
      return { ...prev, emails: [...prev.emails, trimmed] };
    });
    setEmailDraft('');
  }

  function removeEmail(email: string): void {
    setValues((prev) =>
      prev ? { ...prev, emails: prev.emails.filter((entry) => entry !== email) } : prev,
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!values) {
      return;
    }
    const body: AlertSettingsInput = {
      emails: values.emails,
      slackEnabled: values.slackEnabled,
    };
    mutation.mutate(body);
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <PageSection title="Destinataires email">
        <div className="flex flex-col gap-4">
          {values.emails.length > 0 ? (
            <ul className="divide-y divide-border rounded-md bg-muted/50">
              {values.emails.map((email) => (
                <li
                  key={email}
                  className="flex min-h-10 items-center justify-between gap-2 py-1 pl-3 pr-1 text-sm"
                >
                  <span className="min-w-0 break-all">{email}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Retirer ${email}`}
                    onClick={() => {
                      removeEmail(email);
                    }}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Aucun destinataire.</p>
          )}
          <div className="flex items-center gap-2 sm:max-w-md">
            <Input
              type="email"
              placeholder="destinataire@exemple.com"
              aria-label="Nouvelle adresse email"
              value={emailDraft}
              onChange={(event) => {
                setEmailDraft(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addEmail();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Ajouter"
              onClick={addEmail}
            >
              <Plus aria-hidden="true" />
            </Button>
          </div>
          <div className="border-t border-border pt-4">
            <TestButton label="Tester l’email" onTest={testAlertEmail} />
          </div>
        </div>
      </PageSection>

      <PageSection
        title="Slack"
        description={
          query.data.slackConfigured ? (
            'Le webhook Slack est configuré côté serveur.'
          ) : (
            <span className="text-status-orange-fg">
              Aucun webhook Slack configuré — contactez un administrateur système.
            </span>
          )
        }
      >
        <div className="flex flex-col gap-4">
          <label className="flex w-fit items-center gap-2.5 text-sm font-medium">
            <Switch
              checked={values.slackEnabled}
              onCheckedChange={(checked) => {
                setValues((prev) => (prev ? { ...prev, slackEnabled: checked } : prev));
              }}
            />
            Activer les alertes Slack
          </label>
          <div className="border-t border-border pt-4">
            <TestButton label="Tester Slack" onTest={testAlertSlack} />
          </div>
        </div>
      </PageSection>

      <div className="flex flex-wrap justify-end gap-2">
        <SaveButton isLoading={mutation.isPending} isDirty={isDirty} />
      </div>
    </form>
  );
}
